import { Coding } from "@medplum/fhirtypes";
import { Badge, Text } from "@chakra-ui/react-v3";
import { HighlightedText } from "./highlighted-text";

export type CodingBadgeProps = {
  coding?: Coding;
  searchQuery?: string;
};

export const ICD_10_SYSTEM = "http://hl7.org/fhir/sid/icd-10-cm";

/**
 * Mapping of FHIR coding system URLs to friendly display names
 */
export const CCSR_SYSTEM = "https://hcup-us.ahrq.gov/toolssoftware/ccsr/ccs_refined.jsp";

/**
 * Mapping of FHIR coding system URLs to friendly display names
 */
const SYSTEM_DISPLAY_NAMES: Record<string, string> = {
  [ICD_10_SYSTEM]: "ICD-10",
  [CCSR_SYSTEM]: "CCSR",
  "http://www.nlm.nih.gov/research/umls/rxnorm": "RxNorm",
  "http://hl7.org/fhir/sid/cvx": "CVX",
  "http://hl7.org/fhir/sid/ndc": "NDC",
  "http://snomed.info/sct": "SNOMED",
  "http://terminology.hl7.org/CodeSystem/ICD-9CM-diagnosiscodes": "ICD-9",
  "http://terminology.hl7.org/CodeSystem-IMO.html": "IMO",
  "http://terminology.hl7.org/CodeSystem/condition-category": "Condition Category",
  "http://loinc.org": "LOINC",
  "https://public.metriport.com/fhir/StructureDefinition/condition-chronicity.json": "Chronicity",
  "http://terminology.hl7.org/CodeSystem/cmshcc": "HCC",
};

/**
 * Get a friendly display name for a FHIR coding system URL
 * Falls back to the last segment of the URL if no mapping exists
 */
export function getSystemDisplayName(system: string): string {
  const mappedName = SYSTEM_DISPLAY_NAMES[system];
  if (mappedName) {
    return mappedName;
  }

  // Fallback: extract last meaningful segment from URL
  const urlParts = system.split("/");
  const lastSegment = urlParts[urlParts.length - 1];

  if (!lastSegment) {
    return system;
  }

  // Remove common file extensions
  const cleanSegment = lastSegment.replace(/\.(json|html|xml)$/, "");

  return cleanSegment || system;
}

/**
 * Get color palette for a coding system
 */
function getColorPalette(system: string): string {
  // HCC codes get purple to match HCC badges
  if (system.includes("cmshcc") || system.includes("condition-hcc")) {
    return "purple";
  }
  // CCSR codes get teal
  if (system.includes("ccsr") || system.includes("ccs_refined")) {
    return "teal";
  }
  // Default to gray for all other coding systems
  return "gray";
}

/**
 * Component for rendering FHIR Coding objects as badges in table cells
 * Displays with subtle system name and prominent code value
 * Supports search highlighting when searchQuery is provided
 */
export function CodingBadge({ coding, searchQuery = "" }: CodingBadgeProps) {
  if (!coding?.system || !coding?.code) {
    return <Text>-</Text>;
  }

  const systemDisplay = getSystemDisplayName(coding.system);
  const colorPalette = getColorPalette(coding.system);

  return (
    <Badge variant="subtle" colorPalette={colorPalette}>
      <Text as="span" opacity={0.7} fontSize="xs" fontWeight="normal">
        <HighlightedText text={systemDisplay} searchQuery={searchQuery} />:
      </Text>{" "}
      <Text as="span" fontWeight="medium">
        <HighlightedText text={coding.code} searchQuery={searchQuery} />
      </Text>
    </Badge>
  );
}
